import React from "react";
import {
  useMessageContext,
  useChatContext,
  useChannelStateContext,
} from "stream-chat-react";
import toast from "react-hot-toast";
import { businessCardService } from "../../services/api";
import ProfileCardPreviewRenderer from "../chat/ProfileCardPreviewRenderer";
import ClubLinkPreviewRenderer from "../chat/ClubLinkPreviewRenderer";
import { isProfileUrl, isTransferPitchHost, hasProfileLink } from "../../utils/profileLinkDetector";
import { hasClubLink } from "../../utils/clubLinkDetector";
import { getDisplayText } from "../chat/noRawLinks";
import Avatar from "../common/Avatar";
import CustomAttachment from "../chat/CustomAttachment";
import { seedFromChannelData } from "../../utils/chatTimer";

// DECISION-024 (Marin, 2026-07-29) — an EXTERNAL link never renders a preview
// card in chat. Message text stays plain text (external URLs are deliberately
// not linkified, see chat/noRawLinks.js:29), so a link that additionally
// produced a large unfurl card was the inconsistency: in a marketplace where
// strangers message across organisations, a non-clickable link is the cheaper
// phishing brake, and a scraped card re-imports the foreign site's branding
// into the thread. T042-3 only BOUNDED that card (132x88); this drops it.
//
// Identifying an unfurl (Stream's OG scraper output) vs. something the user
// actually sent:
//   - a scrape carries og_scrape_url (the scraper always stamps it) and no
//     uploaded asset; sent photos/files carry asset_url / file_url and never
//     og_scrape_url — so an upload can never match here,
//   - the scraper reuses type "image" when the page exposes an og:image and
//     type "url" otherwise; any OTHER type is one of our own card attachments
//     (business-card, radar_player_share, …) and is left alone, which keeps
//     this from ever hiding a TransferPitch card.
const isLinkUnfurl = (attachment) => {
  if (!attachment) return false;
  if (attachment.asset_url || attachment.file_url) return false; // real upload
  const type = attachment.type;
  if (type && type !== "image" && type !== "url") return false; // our own card
  return Boolean(attachment.og_scrape_url) || type === "url";
};

// "Duarte_Medical_Report.pdf" -> "PDF" for the document-bubble meta line
// (design C5). Falls back to "FILE" when the name carries no usable extension.
const fileTypeLabel = (name) => {
  const raw = String(name || "");
  const ext = raw.includes(".") ? raw.split(".").pop() : "";
  return ext && ext.length <= 5 ? ext.toUpperCase() : "FILE";
};

// Bytes -> "2.4 MB" (one decimal, design C5) or "812 KB". Empty when unknown.
const fileSizeLabel = (bytes) => {
  const n = Number(bytes);
  if (!n || Number.isNaN(n)) return "";
  const mb = n / 1024 / 1024;
  if (mb >= 1) return `${mb.toFixed(1)} MB`;
  return `${Math.max(1, Math.round(n / 1024))} KB`;
};

// Escape a user query for safe use inside a RegExp (search terms are arbitrary).
const escapeRegExp = (s) => String(s).replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

// #31 — render `text` with every case-insensitive occurrence of `query` wrapped
// in a highlight <mark> (design 3c "text-highlight marker"). Returns the plain
// string untouched when there's no query, so non-search renders are unchanged.
const highlightQuery = (text, query) => {
  const q = String(query || "").trim();
  if (!q || !text) return text;
  const re = new RegExp(`(${escapeRegExp(q)})`, "ig");
  const parts = String(text).split(re);
  return parts.map((part, i) =>
    part && part.toLowerCase() === q.toLowerCase() ? (
      <mark
        key={i}
        style={{ background: "#FEF0C7", color: "inherit", borderRadius: 3, padding: "0 1px" }}
      >
        {part}
      </mark>
    ) : (
      <React.Fragment key={i}>{part}</React.Fragment>
    )
  );
};

const CustomMessage = () => {
  const { message } = useMessageContext();
  const { client } = useChatContext();
  const { channel, read } = useChannelStateContext();

  // Properly determine if message is from current user
  const isMyMessage = message.user?.id === client.userID;

  // #32 — read-only history (design frame 1o): a frozen (closed) or expired
  // thread dims its whole history and neutralises own bubbles from blue to grey,
  // so the eye reads "this conversation is closed". Frozen comes off channel.data
  // directly; expiry off the shared timer seed (same source the row chip uses).
  const seed = seedFromChannelData(channel?.data || {});
  const isReadOnly = channel?.data?.frozen === true || seed.isExpired === true;

  // #31 — the header broadcasts the active in-chat-search match (message id +
  // query) over a window event; the focused bubble draws a persistent blue
  // outline, highlights the term and shows a small "current match" hint pill.
  const [searchState, setSearchState] = React.useState({
    messageId: null,
    query: "",
  });
  React.useEffect(() => {
    const onSearchState = (e) => {
      const cid = e?.detail?.channelCid;
      if (cid && channel?.cid && cid !== channel.cid) return;
      setSearchState({
        messageId: e?.detail?.messageId || null,
        query: e?.detail?.query || "",
      });
    };
    window.addEventListener("tp:threads:search-state", onSearchState);
    return () =>
      window.removeEventListener("tp:threads:search-state", onSearchState);
  }, [channel?.cid]);
  const isCurrentMatch =
    Boolean(searchState.messageId) && message.id === searchState.messageId;

  // #29 — REAL delivery/read state on own messages (design C5 meta "✓✓"). Uses
  // the Stream channel read map (userId → {last_read}); the same read state that
  // drives the inbox unread counts, so it's authoritative, never faked. My
  // message is "read" once ANY counterpart's last_read reaches its created_at,
  // else "delivered". Null for the counterpart's messages and whenever the read
  // map isn't present (e.g. read receipts disabled) — we then show nothing
  // rather than a guessed tick.
  const readReceipt = (() => {
    if (!isMyMessage || !read || typeof read !== "object") return null;
    const myTime = message.created_at
      ? new Date(message.created_at).getTime()
      : null;
    if (myTime == null || Number.isNaN(myTime)) return null;
    const readByCounterpart = Object.entries(read).some(([uid, info]) => {
      if (String(uid) === String(client.userID)) return false;
      const lastRead = info?.last_read
        ? new Date(info.last_read).getTime()
        : null;
      return lastRead != null && !Number.isNaN(lastRead) && lastRead >= myTime;
    });
    return readByCounterpart ? "read" : "delivered";
  })();

  // NO-RAW-LINKS: the text we DISPLAY hides any platform URL that already
  // renders as a card below (text-derived profile card or a self-contained
  // card attachment). The ORIGINAL message.text still feeds the card
  // renderers and the OG-suppression filter, so nothing about detection or
  // fallback changes — only the visible text line. See chat/noRawLinks.js.
  const displayText = getDisplayText(message);

  // The attachments that actually render, decided once so the wrapper (and its
  // 8px top margin) never survives an empty result — a bare external link must
  // leave the text bubble alone with no stray gap under it. Two reasons an
  // attachment drops out: it is an external link unfurl (DECISION-024), or its
  // URL is a TransferPitch link we already render as a native card below.
  const visibleAttachments = (
    Array.isArray(message.attachments) ? message.attachments : []
  ).filter((attachment) => {
    // DECISION-024 — external link previews are not rendered at all. The URL
    // itself stays visible as text in the bubble (noRawLinks never strips an
    // external link), so nothing is lost by dropping the card.
    if (isLinkUnfurl(attachment)) return false;

    // Check EVERY URL-ish field on the attachment — Stream's OG scraper can
    // populate any of these depending on what the page exposed
    // (og_scrape_url, title_link, the raw `url`, or even just the image URL
    // on the meta tag), and the type field isn't always "url" either.
    const candidateUrls = [
      attachment.og_scrape_url,
      attachment.title_link,
      attachment.url,
      attachment.image_url,
      attachment.thumb_url,
      attachment.asset_url,
    ].filter(Boolean);

    // (A) Direct match: any field IS a TP profile URL — we've already
    //     rendered a native card for it.
    if (candidateUrls.some((u) => isProfileUrl(u))) return false;

    // (B) Indirect duplication: Stream's scraper often follows the SPA's
    //     canonical <meta og:url> back to the homepage. So the attachment
    //     shows the generic "Football's Transfer Marketplace" banner even
    //     though the user actually pasted a /search/player URL. If the
    //     message text already contains a TP profile link, suppress any
    //     attachment whose URLs are on a TransferPitch host.
    if (
      (hasProfileLink(message.text) || hasClubLink(message.text)) &&
      candidateUrls.some((u) => isTransferPitchHost(u))
    ) {
      return false;
    }
    return true;
  });

  // Get sender information
  const sender = message.user;
  const channelUserDetails = Array.isArray(channel?.data?.users)
    ? channel.data.users.find(
        (userEntry) => String(userEntry.id) === String(sender?.id)
      )
    : null;
  const memberUser = channel?.state?.members?.[sender?.id]?.user;
  const senderAvatar =
    channelUserDetails?.profile_picture_url ||
    channelUserDetails?.image ||
    memberUser?.profile_picture_url ||
    memberUser?.image ||
    sender?.profile_picture_url ||
    sender?.image ||
    sender?.profile_image_url ||
    null;
  const senderName =
    channelUserDetails?.name ||
    channelUserDetails?.full_name ||
    memberUser?.name ||
    sender?.name ||
    sender?.id ||
    "Unknown User";

  // Absolute, person-led timestamp under each bubble (design C1): "You · 15:12"
  // for own messages, "<FirstName> · 15:12" for the counterpart, with the day
  // added once the message is older than today ("Sofia · 24 Jun, 14:05").
  // Replaces the relative "N minutes ago" wording. Inbox-list preview times are
  // owned by CustomChannelPreview and are not touched here.
  const formatStamp = (timestamp) => {
    if (!timestamp) return "";
    const date = new Date(timestamp);
    const label = isMyMessage
      ? "You"
      : String(senderName || "").trim().split(/\s+/)[0] || senderName;
    const time = date.toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    });
    const now = new Date();
    const sameDay =
      date.getFullYear() === now.getFullYear() &&
      date.getMonth() === now.getMonth() &&
      date.getDate() === now.getDate();
    if (sameDay) return `${label} · ${time}`;
    const mon = date.toLocaleString("en-US", { month: "short" });
    return `${label} · ${date.getDate()} ${mon}, ${time}`;
  };

  // Don't render deleted messages
  if (message.type === "deleted") {
    return (
      <div className="flex items-center justify-center py-2">
        <span className="text-xs text-gray-400 italic">Message deleted</span>
      </div>
    );
  }

  // System / handover event lines (design frame 5d) — posted server-side via
  // post_system_line (type === "system"): member added, left, contact-person /
  // timer handover. Rendered as a quiet, centered card; the backend formats the
  // copy, this branch only styles it (so any system line renders consistently).
  if (message.type === "system" && message.text) {
    // #27-FE — viewer-relative accepted-request line. The backend posts
    // "<Accepter> accepted <Sender>'s request" with the custom field
    // `accepter_account_id`; when that's the logged-in user, swap the leading
    // accepter name for "You" ("You accepted <Sender>'s request"). Everyone
    // else sees the third-person copy verbatim. Non-greedy match stops at the
    // first " accepted ", so the sender's name after it is preserved.
    let systemText = message.text;
    const accepterId = message.accepter_account_id;
    if (
      accepterId != null &&
      String(accepterId) === String(client.userID) &&
      / accepted /.test(systemText)
    ) {
      systemText = systemText.replace(/^.+? accepted /, "You accepted ");
    }

    const eventTime = message.created_at
      ? new Date(message.created_at).toLocaleTimeString([], {
          hour: "2-digit",
          minute: "2-digit",
          hour12: false,
        })
      : "";
    // Several 5d lines already carry "· HH:MM" in the server copy (e.g.
    // "… left the conversation · 16:40"); don't append the timestamp again.
    const textHasInlineTime = /·\s*\d{1,2}:\d{2}/.test(systemText);
    const showTime = Boolean(eventTime) && !textHasInlineTime;

    // The "added member" line renders as a 999px pill with an inline person+
    // icon and inline time (design 5d, line ~240), distinct from the rounded
    // card used for the left / handover lines (those stay as-is).
    const isAddedEvent = /\sadded\s/i.test(systemText);
    if (isAddedEvent) {
      return (
        <div className="flex justify-center py-2">
          <div
            className="inline-flex items-center"
            style={{
              gap: 8,
              background: "#F8FAFC",
              border: "1px solid #E9EDF2",
              borderRadius: 999,
              padding: "6px 14px",
              maxWidth: "85%",
            }}
          >
            <span
              aria-hidden="true"
              style={{ color: "#475467", display: "inline-flex", flexShrink: 0 }}
            >
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
                <circle cx="9" cy="7" r="4" />
                <path d="M19 8v6M22 11h-6" />
              </svg>
            </span>
            <span style={{ fontSize: 11.5, color: "#475467", lineHeight: 1.4 }}>
              {systemText}
            </span>
            {showTime && (
              <span
                style={{ fontSize: 10, color: "#98A2B3", flexShrink: 0 }}
              >
                {eventTime}
              </span>
            )}
          </div>
        </div>
      );
    }

    return (
      <div className="flex justify-center py-2">
        <div
          className="flex flex-col items-center text-center"
          style={{
            gap: 2,
            background: "#F8FAFC",
            border: "1px solid #E9EDF2",
            borderRadius: 14,
            padding: "7px 16px",
            maxWidth: "80%",
          }}
        >
          <span
            className="whitespace-pre-line"
            style={{ fontSize: 11.5, color: "#475467", lineHeight: 1.4 }}
          >
            {systemText}
          </span>
          {showTime && (
            <span style={{ fontSize: 10, color: "#98A2B3" }}>{eventTime}</span>
          )}
        </div>
      </div>
    );
  }

  // Call events (design frame 3e) — the call/video send path posts a
  // `call_invitation` attachment carrying {call_type, call_outcome,
  // call_duration_seconds}. Rendered as quiet event cards (not chat bubbles):
  // state + direction + duration at a glance, red reserved for Missed only, one
  // action where one makes sense (Call back / Retry). Render-only: the send path
  // (CustomChatHeader) and the incoming-call detection (useInbox `message.new`)
  // are untouched — this branch only reads the attachment fields.
  const callAttachment = Array.isArray(message.attachments)
    ? message.attachments.find((a) => a?.type === "call_invitation")
    : null;
  if (callAttachment) {
    const isVideo = callAttachment.call_type === "video";
    const kind = isVideo ? "Video call" : "Audio call";
    const outcome = callAttachment.call_outcome || null;

    // Duration → "12 min 40 s" (design 3e). Under a minute drops to "40 s".
    const durationSeconds = Number(callAttachment.call_duration_seconds);
    const durationLabel =
      Number.isFinite(durationSeconds) && durationSeconds > 0
        ? durationSeconds >= 60
          ? `${Math.floor(durationSeconds / 60)} min ${durationSeconds % 60} s`
          : `${durationSeconds} s`
        : null;

    const callerName = callAttachment.caller_name || null;

    // Whose perspective is this? The caller is the message sender; prefer the
    // explicit caller_id stamped on the attachment (send path), falling back to
    // isMyMessage for historic/fixture messages that carry no caller_id. ONE
    // stored outcome drives both perspectives — the renderer, not the backend,
    // splits missed→(Missed vs No answer) and outgoing→(Outgoing vs Incoming).
    const viewerIsCaller =
      callAttachment.caller_id != null
        ? String(callAttachment.caller_id) === String(client.userID)
        : isMyMessage;

    // Neutral palette is the default; only Missed goes red (design 3e). "Call
    // back" (missed) and "Retry" (no_answer) reuse the header's call-start
    // handlers via a decoupled window event (see CustomChatHeader listener).
    let title = kind;
    let subtitle = null;
    let action = null;
    let borderColor = "#E4E9F0";
    let iconBg = "#F2F4F7";
    let iconColor = "#475467";
    let titleColor = "#111827";

    if (outcome === "missed") {
      if (viewerIsCaller) {
        // Caller side of a never-answered call: the "No answer + Retry" optic
        // (design 3e no-answer), not the red missed card.
        title = "No answer";
        subtitle = `Outgoing ${isVideo ? "video" : "audio"} call`;
        action = { label: "Retry", accent: false };
        iconColor = "#667085";
        titleColor = "#475467";
      } else {
        // Recipient side: the red missed card + Call back (design 3e missed).
        title = `Missed ${isVideo ? "video" : "audio"} call`;
        subtitle = callerName
          ? `${callerName} tried to reach you`
          : "You missed this call";
        action = { label: "Call back", accent: true };
        borderColor = "#F3D2CE";
        iconBg = "#FCEDEB";
        iconColor = "#C2362B";
        titleColor = "#C2362B";
      }
    } else if (outcome === "declined") {
      title = `${kind} declined`;
      // Caller reads the "no charge to the timer" reassurance; the decliner
      // (recipient) gets the neutral title with no outgoing subline.
      subtitle = viewerIsCaller ? "Outgoing · no charge to the timer" : null;
      iconColor = "#667085";
      titleColor = "#475467";
    } else if (outcome === "no_answer") {
      // Legacy / fixture outcome — kept verbatim for backward compatibility.
      title = "No answer";
      subtitle = `Outgoing ${isVideo ? "video" : "audio"} call`;
      action = { label: "Retry", accent: false };
      iconColor = "#667085";
      titleColor = "#475467";
    } else if (outcome === "outgoing" || outcome === "completed") {
      // Answered/finished call. Caller sees "Outgoing · <dur>" on a neutral
      // card; the recipient sees the #19 finished-incoming card: blue icon,
      // "Incoming · <dur>" (design 3e finished-incoming).
      title = kind;
      if (viewerIsCaller) {
        subtitle = durationLabel ? `Outgoing · ${durationLabel}` : "Outgoing";
      } else {
        subtitle = durationLabel ? `Incoming · ${durationLabel}` : "Incoming";
        iconBg = "#EAF2FF";
        iconColor = "#1466D8";
        titleColor = "#111827";
      }
    }
    // else: no outcome field (historic invitation messages) → neutral card with
    // the "Audio call" / "Video call" wording family, never the old text.

    const eventTime = message.created_at
      ? new Date(message.created_at).toLocaleTimeString([], {
          hour: "2-digit",
          minute: "2-digit",
          hour12: false,
        })
      : "";

    // Direction drives left/right alignment relative to the VIEWER: a call the
    // viewer placed (caller) sits right (outgoing), one they received sits left
    // (incoming) — so the missed→caller "No answer" card lands right while the
    // recipient's "Missed" card lands left, matching design 3e. An outcome-less
    // historic card stays centered (direction unknown).
    const alignClass = !outcome
      ? "justify-center"
      : viewerIsCaller
      ? "justify-end"
      : "justify-start";

    return (
      <div className={`flex ${alignClass} py-2`}>
        <div
          className="flex items-center"
          style={{
            gap: 11,
            background: "#fff",
            border: `1px solid ${borderColor}`,
            borderRadius: 12,
            padding: "9px 12px",
            minWidth: 280,
            maxWidth: "80%",
          }}
        >
          <span
            aria-hidden="true"
            style={{
              width: 36,
              height: 36,
              borderRadius: 999,
              background: iconBg,
              color: iconColor,
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            {isVideo ? (
              <svg width="15" height="15" viewBox="0 0 15 15" fill="none">
                <rect
                  x="1.8"
                  y="4"
                  width="8.4"
                  height="7"
                  rx="1.8"
                  stroke="currentColor"
                  strokeWidth="1.4"
                />
                <path
                  d="M10.2 7.7l3-1.9v3.6l-3-1.7Z"
                  stroke="currentColor"
                  strokeWidth="1.4"
                  strokeLinejoin="round"
                />
              </svg>
            ) : (
              <svg width="15" height="15" viewBox="0 0 15 15" fill="none">
                <path
                  d="M4.8 2.8c-1 0-1.9.8-1.9 1.9 0 4.1 3.3 7.4 7.4 7.4 1 0 1.9-.8 1.9-1.9v-1l-2.4-.9-1 1a7.6 7.6 0 0 1-3-3l1-1-.9-2.4h-1Z"
                  stroke="currentColor"
                  strokeWidth="1.4"
                  strokeLinejoin="round"
                />
              </svg>
            )}
          </span>
          <span
            style={{
              flex: 1,
              display: "flex",
              flexDirection: "column",
              gap: 1,
              minWidth: 0,
            }}
          >
            <span
              style={{ fontSize: 13, fontWeight: 600, color: titleColor }}
            >
              {title}
            </span>
            {subtitle && (
              <span style={{ fontSize: 11.5, color: "#667085" }}>
                {subtitle}
              </span>
            )}
          </span>
          {action && (
            <button
              type="button"
              onClick={() =>
                window.dispatchEvent(
                  new CustomEvent("tp:threads:start-call", {
                    detail: { call_type: isVideo ? "video" : "audio" },
                  })
                )
              }
              style={{
                flexShrink: 0,
                padding: "5px 12px",
                borderRadius: 8,
                background: "#fff",
                border: `1px solid ${action.accent ? "#BBD7FF" : "#E4E9F0"}`,
                color: action.accent ? "#1466D8" : "#475467",
                fontSize: 11.5,
                fontWeight: 600,
                cursor: "pointer",
              }}
            >
              {action.label}
            </button>
          )}
          {eventTime && (
            <span
              style={{ fontSize: 10.5, color: "#98A2B3", flexShrink: 0 }}
            >
              {eventTime}
            </span>
          )}
        </div>
      </div>
    );
  }

  return (
    <div
      className={`flex items-start gap-3 mb-4 ${
        isMyMessage ? "flex-row-reverse" : "flex-row"
      }`}
      style={isReadOnly ? { opacity: 0.75 } : undefined}
    >
      {/* Avatar — counterpart only. Own messages carry no avatar (design C1 /
          parity #6); the blue bubble alone reads as "mine". */}
      {!isMyMessage && (
        <div className="flex-shrink-0">
          <Avatar
            src={senderAvatar}
            alt={senderName}
            size={32}
            fallbackText={senderName}
          />
        </div>
      )}

      {/* Message Content */}
      <div
        className={`flex flex-col max-w-[70%] ${
          isMyMessage ? "items-end" : "items-start"
        }`}
      >
        {/* Text bubble — design C1: own = solid #2283FF / white text /
            radius 14 14 4 14; counterpart = white / #E4E9F0 border /
            radius 14 14 14 4; 13.5px. The sender name moves to the meta line
            below (design puts identity in the timestamp, not inside the
            bubble). Chrome wraps TEXT ONLY — attachment cards and profile
            previews render standalone below, never inside a colored bubble
            (parity #4). */}
        {displayText && (
          <div
            className={`px-[14px] py-[10px] text-[13.5px] break-words overflow-wrap-anywhere ${
              isMyMessage
                ? isReadOnly
                  ? "bg-[#E4E9F0] text-[#475467] rounded-[14px_14px_4px_14px]"
                  : "bg-[#2283FF] text-white rounded-[14px_14px_4px_14px]"
                : "bg-white text-[#344054] border border-[#E4E9F0] rounded-[14px_14px_14px_4px]"
            } ${
              isCurrentMatch
                ? "ring-2 ring-[#2283FF] ring-offset-2 ring-offset-white"
                : ""
            }`}
            style={{
              wordWrap: "break-word",
              overflowWrap: "anywhere",
            }}
            data-testid={isCurrentMatch ? "chat-search-current-match" : undefined}
          >
            <p className="text-[13.5px] leading-relaxed">
              {isCurrentMatch
                ? highlightQuery(displayText, searchState.query)
                : displayText}
            </p>
          </div>
        )}

        {/* #31 — hint pill on the focused search match (design 3c). */}
        {isCurrentMatch && (
          <span
            className="mt-1 inline-flex items-center gap-1 rounded-full bg-[#EAF2FF] px-2 py-0.5 text-[10px] font-semibold text-[#1466D8]"
            data-testid="chat-search-current-hint"
          >
            current match · jump with ↑ ↓
          </span>
        )}

        {/* Profile-link preview — if the message text contains a TransferPitch
            /search/player/:id or /search/coach/:id URL, render a card preview
            below the bubble. Short-circuits to null for non-matching messages,
            so the 99% case is zero cost. */}
        {message.text && <ProfileCardPreviewRenderer text={message.text} />}

        {/* Club / agency link preview (#18) — the same treatment for
            /search/club/:id and /search/agency/:id links: an identity card
            instead of a raw URL + generic OG banner. Self-heals to a link. */}
        {message.text && <ClubLinkPreviewRenderer text={message.text} />}

        {/* Attachments — `visibleAttachments` above already dropped the ones
            that must not render: external link unfurls (DECISION-024) and OG
            previews whose URL we already render as a native TransferPitch
            card. */}
          {visibleAttachments.length > 0 && (
            <div className="mt-2">
              {visibleAttachments
                .map((attachment, index) => (
                <div key={index} className="mb-2">
                  {/* Photo bubble — design C6: white bubble, #E4E9F0 border,
                      280x150 frame, side-specific radius. */}
                  {attachment.type === "image" && (
                    <div
                      className={`inline-block overflow-hidden bg-white border border-[#E4E9F0] ${
                        isMyMessage
                          ? "rounded-[14px_14px_4px_14px]"
                          : "rounded-[14px_14px_14px_4px]"
                      }`}
                    >
                      <img
                        src={
                          attachment.image_url ||
                          attachment.asset_url ||
                          attachment.thumb_url
                        }
                        alt={attachment.title || attachment.name || "Image"}
                        className="block cursor-pointer hover:opacity-95 transition-opacity"
                        /* Design C6's photo frame. Only genuine uploads reach
                           this branch now — an external link unfurl (which
                           reuses type "image" when the page exposes an
                           og:image) is dropped by `visibleAttachments`, so no
                           second, bounded variant is needed here any more
                           (DECISION-024 replaces T042-3). */
                        style={{
                          width: 280,
                          maxWidth: "100%",
                          height: 150,
                          objectFit: "cover",
                        }}
                        onClick={() =>
                          window.open(
                            attachment.image_url || attachment.asset_url,
                            "_blank"
                          )
                        }
                        onError={(e) => {
                          e.target.style.display = "none";
                        }}
                      />
                    </div>
                  )}

                  {/* Document bubble — design C5: icon tile + filename +
                      "PDF · 2.4 MB" + a round download affordance. Own = solid
                      #2283FF/white; counterpart = white with #E4E9F0 border. */}
                  {(attachment.type === "file" ||
                    (!attachment.type && attachment.asset_url)) && (
                    <a
                      href={attachment.asset_url || attachment.file_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={`flex items-center gap-[11px] p-[9px] min-w-[240px] max-w-[320px] transition-opacity hover:opacity-90 ${
                        isMyMessage
                          ? "bg-[#2283FF] rounded-[14px_14px_4px_14px]"
                          : "bg-white border border-[#E4E9F0] rounded-[14px_14px_14px_4px]"
                      }`}
                    >
                      <div
                        className={`w-10 h-10 rounded-[10px] flex items-center justify-center flex-shrink-0 ${
                          isMyMessage ? "bg-white/[0.18]" : "bg-[#F2F4F7]"
                        }`}
                      >
                        <svg width="17" height="17" viewBox="0 0 16 16" fill="none">
                          <path
                            d="M4 1.8h5.2L12.5 5v9.2H4V1.8Z"
                            stroke={isMyMessage ? "#fff" : "#475467"}
                            strokeWidth="1.4"
                            strokeLinejoin="round"
                          />
                          <path
                            d="M9 2v3.2h3.2"
                            stroke={isMyMessage ? "#fff" : "#475467"}
                            strokeWidth="1.4"
                            strokeLinejoin="round"
                          />
                        </svg>
                      </div>
                      <div className="flex-1 min-w-0 flex flex-col gap-px">
                        <span
                          className={`text-[13px] font-semibold truncate ${
                            isMyMessage ? "text-white" : "text-[#344054]"
                          }`}
                        >
                          {attachment.title || attachment.name || "File"}
                        </span>
                        <span
                          className={`text-[11px] ${
                            isMyMessage ? "text-white/75" : "text-[#98A2B3]"
                          }`}
                        >
                          {[
                            fileTypeLabel(attachment.title || attachment.name),
                            fileSizeLabel(attachment.file_size),
                          ]
                            .filter(Boolean)
                            .join(" · ")}
                        </span>
                      </div>
                      <div
                        className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                          isMyMessage ? "bg-white/[0.18]" : "bg-[#F2F4F7]"
                        }`}
                      >
                        <svg width="13" height="13" viewBox="0 0 12 12" fill="none">
                          <path
                            d="M6 2v6M3.5 5.7L6 8.2 8.5 5.7M2.5 10h7"
                            stroke={isMyMessage ? "#fff" : "#475467"}
                            strokeWidth="1.4"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      </div>
                    </a>
                  )}

                  {/* Video Attachments */}
                  {attachment.type === "video" && (
                    <div className="rounded-lg overflow-hidden">
                      <video
                        controls
                        className="max-w-full h-auto"
                        style={{ maxHeight: "300px" }}
                        poster={attachment.thumb_url}
                      >
                        <source
                          src={attachment.asset_url}
                          type={attachment.mime_type || "video/mp4"}
                        />
                        Your browser does not support the video tag.
                      </video>
                    </div>
                  )}

                  {/* Audio Attachments */}
                  {attachment.type === "audio" && (
                    <div
                      className={`p-3 rounded-lg ${
                        isMyMessage ? "bg-white bg-opacity-20" : "bg-gray-50"
                      }`}
                    >
                      <audio controls className="w-full">
                        <source
                          src={attachment.asset_url}
                          type={attachment.mime_type || "audio/mpeg"}
                        />
                        Your browser does not support the audio tag.
                      </audio>
                    </div>
                  )}

                  {/* Business Card Attachments */}
                  {attachment.type === "business-card" && (
                    <div
                      className={`p-3 rounded-lg border ${
                        isMyMessage
                          ? "border-white border-opacity-30"
                          : "border-gray-200"
                      }`}
                    >
                      <div className="flex items-center gap-2">
                        <svg
                          className="w-5 h-5"
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path
                            fillRule="evenodd"
                            d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z"
                            clipRule="evenodd"
                          />
                        </svg>
                        <span className="text-sm font-medium">
                          Business Card
                        </span>
                      </div>
                      {attachment.html && (
                        <div
                          className="mt-2 text-xs"
                          dangerouslySetInnerHTML={{ __html: attachment.html }}
                        />
                      )}
                      {/* Save button - only show for receivers */}
                      {!isMyMessage && attachment.business_card_id && (
                        <div className="mt-3 flex justify-end">
                          <button
                            onClick={async () => {
                              console.log(
                                "Business Card ID:",
                                attachment.business_card_id
                              );
                              try {
                                const result =
                                  await businessCardService.saveBusinessCard(
                                    attachment.business_card_id
                                  );
                                if (result.success) {
                                  toast.success("Contact saved successfully!");
                                } else {
                                  // Extract error message properly - API returns {detail: "message"} in error response
                                  const errorMessage =
                                    result.error?.detail ||
                                    result.error ||
                                    "Failed to save contact";

                                  // Handle specific error message for existing contact
                                  if (
                                    errorMessage === "Contact already exists."
                                  ) {
                                    toast.error("Contact already exists.");
                                  } else {
                                    toast.error(errorMessage);
                                  }
                                }
                              } catch (error) {
                                console.error(
                                  "Error saving business card:",
                                  error
                                );
                                toast.error("Failed to save contact");
                              }
                            }}
                            className="px-3 py-1.5 text-xs font-medium text-blue-600 bg-blue-50 hover:bg-blue-100 border border-blue-200 rounded-md transition-colors"
                          >
                            Save Contact
                          </button>
                        </div>
                      )}
                    </div>
                  )}

                  {/* DECISION-024 — there is deliberately NO renderer for the
                      link-preview attachment type ("url"). Stream's unfurl is
                      dropped by `visibleAttachments` before it gets here, and
                      "url" stays in the exclusion list below so a stray one can
                      never fall through to a share card either. The link stays
                      readable as text in the bubble. */}

                  {/* Custom / share attachment types (radar player + shortlist
                      shares, scout request + recommendation forward, last-minute
                      listing, friendly request, player_share, generic share,
                      business_card, …) are delegated to the CustomAttachment
                      dispatch, which routes each to its dedicated card and, for
                      any type it doesn't recognise, to FallbackShareCard — so an
                      unknown share can never render an empty bubble. Delegated
                      one attachment at a time to preserve ordering when a single
                      message mixes types. The inline branches above still win
                      for their own types (image/file/video/audio/business-card),
                      and "url" is excluded because it renders NOTHING by
                      decision; a truthy type is required here so the file
                      branch's `!type && asset_url` case is never
                      double-rendered. */}
                  {attachment.type &&
                    ![
                      "image",
                      "file",
                      "video",
                      "audio",
                      "business-card",
                      "url",
                    ].includes(attachment.type) && (
                      <CustomAttachment attachments={[attachment]} />
                    )}
                </div>
              ))}
            </div>
          )}

        {/* Timestamp — design C1 meta line (10.5px #98A2B3). Own messages carry
            the real delivery/read tick after the time ("You · 10:02 ✓✓"). */}
        <span
          className={`text-[10.5px] text-[#98A2B3] mt-1.5 px-2 font-normal ${
            isMyMessage ? "text-right" : "text-left"
          }`}
        >
          {formatStamp(message.created_at)}
          {readReceipt && (
            <span
              className={`ml-1 ${
                readReceipt === "read" ? "text-[#2283FF]" : "text-[#98A2B3]"
              }`}
              aria-label={readReceipt === "read" ? "Read" : "Delivered"}
              title={readReceipt === "read" ? "Read" : "Delivered"}
            >
              {readReceipt === "read" ? "✓✓" : "✓"}
            </span>
          )}
        </span>
      </div>
    </div>
  );
};

export default CustomMessage;
